import React, { useEffect, useState, useCallback } from 'react';
import { Star, TrendingUp, TrendingDown, ArrowUpDown, Search } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';

interface TickerData {
  symbol: string; mark_price: number; index_price: number;
  best_bid: number; best_ask: number; spread_pct: number;
  est_funding: number; next_funding: number;
  open_interest: number; volume_24h: number; price_change_24h: number;
}
interface Props { dark?: boolean; }

const FAVORITES_KEY = 'raydium_favorites';

export function getFavorites(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]'); }
  catch { return []; }
}

export function setFavorites(favs: string[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
  window.dispatchEvent(new Event('favorites_changed'));
}

type SortKey = 'volume' | 'alpha' | 'change' | 'funding' | 'oi';
type SortDir = 'asc' | 'desc';

export default function Markets({ dark = true }: Props) {
  const { tickers: wsTickers, connected, sendWs } = useWS();
  const tickers = wsTickers as unknown as TickerData[];
  const [favorites, setFavs]      = useState<string[]>(getFavorites);
  const [search, setSearch]       = useState('');
  const [sortKey, setSortKey]     = useState<SortKey>('volume');
  const [sortDir, setSortDir]     = useState<SortDir>('desc');
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const t = getTheme(dark);

  // Başka sekme / sunucu restore_state'den favorites değişince güncelle
  useEffect(() => {
    const onStorage = () => setFavs(getFavorites());
    window.addEventListener('favorites_changed', onStorage);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('favorites_changed', onStorage);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggleFav = useCallback((symbol: string) => {
    setFavs(prev => {
      const next = prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol];
      setFavorites(next);
      sendWs({ type: 'update_favorites', payload: next });
      return next;
    });
  }, [sendWs]);

  const cycleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const displayName = (sym: string) => sym.replace('PERP_', '').replace('_USDC', '');

  const filtered = tickers
    .filter(tk => {
      if (showFavsOnly && !favorites.includes(tk.symbol)) return false;
      if (search && !displayName(tk.symbol).toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let va = 0, vb = 0;
      if (sortKey === 'volume')  { va = a.volume_24h || 0;       vb = b.volume_24h || 0; }
      if (sortKey === 'alpha')   { return sortDir === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol); }
      if (sortKey === 'change')  { va = a.price_change_24h || 0; vb = b.price_change_24h || 0; }
      if (sortKey === 'funding') { va = a.est_funding || 0;      vb = b.est_funding || 0; }
      if (sortKey === 'oi')      { va = a.open_interest || 0;    vb = b.open_interest || 0; }
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  // Favoriler her zaman üstte
  const sorted = [
    ...filtered.filter(tk => favorites.includes(tk.symbol)),
    ...filtered.filter(tk => !favorites.includes(tk.symbol)),
  ];

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button onClick={() => cycleSort(k)}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
        sortKey === k
          ? 'bg-[#4A9FE8] text-[#061530] font-bold'
          : `${t.btnSecondary} opacity-70`
      }`}>
      {label}
      {sortKey === k && <ArrowUpDown size={10} />}
    </button>
  );

  const card = `${t.card} border ${t.cardBorder} rounded-xl`;

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${t.text}`}>Piyasa Verileri</h2>
          <p className={`text-xs mt-0.5 ${t.muted}`}>Orderly Network · Raydium Perps</p>
        </div>
        <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
          {connected ? `Canlı · ${tickers.length} enstrüman` : 'Bağlantı bekleniyor...'}
        </div>
      </div>

      {/* Kontroller */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Arama */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'}`}>
          <Search size={13} className={t.muted} />
          <input
            placeholder="Sembol ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`bg-transparent text-xs outline-none w-28 ${t.text} placeholder:${t.muted}`}
          />
        </div>

        {/* Favoriler filtresi */}
        <button onClick={() => setShowFavsOnly(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showFavsOnly ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
          }`}>
          <Star size={12} fill={showFavsOnly ? 'currentColor' : 'none'} />
          Favoriler {favorites.length > 0 && `(${favorites.length})`}
        </button>

        <div className={`h-4 w-px ${dark ? 'bg-[#0a2560]' : 'bg-blue-200'}`} />

        {/* Sıralama */}
        <span className={`text-xs ${t.muted}`}>Sırala:</span>
        <SortBtn k="volume"  label="Hacim" />
        <SortBtn k="alpha"   label="A-Z" />
        <SortBtn k="change"  label="24s %" />
        <SortBtn k="funding" label="Funding" />
        <SortBtn k="oi"      label="OI" />
      </div>

      {/* Favori sayısı uyarısı */}
      {favorites.length === 0 && (
        <div className={`${card} p-3 flex items-center gap-2`}>
          <Star size={14} className="text-[#4A9FE8]" />
          <p className={`text-xs ${t.muted}`}>
            Henüz favori eklenmedi. Yıldıza tıklayarak enstrüman ekle — Dashboard sadece favorileri gösterir.
          </p>
        </div>
      )}

      {/* Tablo */}
      <div className={`${card} overflow-hidden`}>
        {sorted.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-sm`}>
            {connected ? (showFavsOnly ? 'Favori enstrüman yok.' : 'Eşleşen enstrüman bulunamadı.') : 'Backend bağlantısı bekleniyor...'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  <th className="px-3 py-2.5 text-left w-8"></th>
                  <th className="px-3 py-2.5 text-left">Sembol</th>
                  <th className="px-3 py-2.5 text-right">Mark</th>
                  <th className="px-3 py-2.5 text-right">24s Değişim</th>
                  <th className="px-3 py-2.5 text-right">Funding Est.</th>
                  <th className="px-3 py-2.5 text-right">Spread %</th>
                  <th className="px-3 py-2.5 text-right">OI</th>
                  <th className="px-3 py-2.5 text-right">24s Hacim</th>
                  <th className="px-3 py-2.5 text-right">Bid</th>
                  <th className="px-3 py-2.5 text-right">Ask</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(tk => {
                  const isFav = favorites.includes(tk.symbol);
                  return (
                    <tr key={tk.symbol} className={`border-b ${t.tableRow} ${isFav ? (dark ? 'bg-[#4A9FE8]/5' : 'bg-yellow-50/60') : ''}`}>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleFav(tk.symbol)}
                          className={`transition-colors ${isFav ? 'text-[#4A9FE8]' : `${t.muted} hover:text-[#4A9FE8]`}`}>
                          <Star size={13} fill={isFav ? 'currentColor' : 'none'} />
                        </button>
                      </td>
                      <td className={`px-3 py-2 font-semibold ${t.gold}`}>
                        {displayName(tk.symbol)}
                        {isFav && <span className="ml-1 text-[10px] text-[#4A9FE8] opacity-60">★</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${t.text}`}>${tk.mark_price?.toFixed(4)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${(tk.price_change_24h || 0) >= 0 ? t.positive : t.negative}`}>
                        {(tk.price_change_24h || 0) >= 0 ? '+' : ''}{tk.price_change_24h?.toFixed(2)}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${(tk.est_funding || 0) >= 0 ? t.positive : t.negative}`}>
                        {tk.est_funding?.toFixed(6)}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${(tk.spread_pct || 0) > 0.5 ? t.negative : t.muted}`}>
                        {tk.spread_pct?.toFixed(4)}%
                      </td>
                      <td className={`px-3 py-2 text-right ${t.muted}`}>${((tk.open_interest || 0)/1e6).toFixed(2)}M</td>
                      <td className={`px-3 py-2 text-right font-semibold ${t.text}`}>${((tk.volume_24h || 0)/1e6).toFixed(2)}M</td>
                      <td className={`px-3 py-2 text-right font-mono ${t.positive}`}>${tk.best_bid?.toFixed(4)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${t.negative}`}>${tk.best_ask?.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
